import axios from "axios";

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error("API Response Error:", error);

    if (error.response?.status === 404) {
      console.warn("Resource not found");
    } else if (error.response?.status >= 500) {
      console.error("Server error");
    }

    return Promise.reject(error);
  }
);

// Players API
export const playersAPI = {
  getAll: () => api.get("/players"),
  create: (player) => api.post("/players", player),
  update: (id, player) => api.put(`/players/${id}`, player),
  delete: (id) => api.delete(`/players/${id}`),
};

// Weeks API
export const weeksAPI = {
  getAll: () => api.get("/weeks"),
  //getActive: () => api.get("/weeks/active"),
  //create: (week) => api.post("/weeks", week),
  //update: (id, week) => api.put(`/weeks/${id}`, week),
  //delete: (id) => api.delete(`/weeks/${id}`),
  //checkAndAdvance: () => api.post("/weeks/advance"),
};

// Games API
export const gamesAPI = {
  getAll: (weekId, { selectedOnly = false } = {}) =>
    api.get("/games", {
      params: {
        ...(weekId ? { week_id: weekId } : {}),
        ...(selectedOnly ? { selected: 1 } : {}),
      },
    }),
  getActiveWeekGames: () => api.get("/games/active"),
  create: (game) => api.post("/games", game),
  update: (id, game) => api.put(`/games/${id}`, game),
  delete: (id) => api.delete(`/games/${id}`),
};

// Picks API
export const picksAPI = {
  getAll: (params = {}) => api.get("/picks", { params }),
  getWeekPickCounts: (weekId) => api.get(`/picks/week/${weekId}/counts`),
  getWeekSummary: (weekId) => api.get(`/picks/week/${weekId}/summary`),
  checkDuplicates: (playerId, weekId, currentPicks = null) => {
    const data = currentPicks ? { current_picks: currentPicks } : {};
    return api.post(
      `/picks/player/${playerId}/week/${weekId}/duplicates`,
      data
    );
  },
  create: (pick) => api.post("/picks", pick),
  submitWeekPicks: (playerID, picks) =>
    api.post("/picks/week", { player_id: playerID, picks }),
  update: (id, pick) => api.put(`/picks/${id}`, pick),
  delete: (id) => api.delete(`/picks/${id}`),
};

// Standings API
export const standingsAPI = {
  getAll: () => api.get("/standings"),
  getPlayer: (playerId) => api.get(`/standings/player/${playerId}`),
  getWeek: (weekId) => api.get(`/standings/week/${weekId}`),
};

// Admin API
export const adminAPI = {
  establishSeason: (season) =>
    api.post(`/admin/cfbd/establishSeason/${season}`),
  // Same endpoint as establishSeason -- re-running it refreshes scores/spreads.
  refreshFromCFBD: (season) =>
    api.post(`/admin/cfbd/establishSeason/${season}`),
  getCFBDStatus: () => api.get(`/admin/cfbd/status`),
};

// Selections API
export const selectionsAPI = {
  getSelectedGames: (weekId) => api.get(`/selections/week/${weekId}`),
  saveSelectedGames: (weekId, gameIds) =>
    api.post(`/selections/week/${weekId}`, { game_ids: gameIds }),
};

// Seasons API
export const seasonsAPI = {
  archiveSeason: () => api.post('/seasons/archive'),
  getArchivedSeasons: () => api.get('/seasons/archived'),
  getArchivedSeasonDetails: (season) => api.get(`/seasons/archived/${season}`),
};

// Utility functions
export const handleApiError = (error, defaultMessage = "An error occurred") => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  return defaultMessage;
};

export const formatGameTime = (gameTime) => {
  const date = new Date(gameTime);
  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

export const calculatePercentage = (correct, total) => {
  if (total === 0) return 0;
  return Math.round((correct / total) * 100 * 100) / 100; // Round to 2 decimal places
};

export default api;
